import { useState, useCallback } from "react";
import {
  ApiDataResponse,
  ApiResponse,
  AssessmentType,
} from "../../services/Dtos";
import { ApiService } from "../../services/ApiService";
import useAxios from "../useAxios";
import { useSchoolsContext } from "../../Contexts/schoolContext";
import { useApiResponseHandler } from "../useApiResponseHandler";

export const useAssessmentTypeService = () => {
  const { setAssessmentTypes } = useSchoolsContext();
  const axiosClient = useAxios();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const { handleApiError, handleApiSuccess, clearErrors } =
    useApiResponseHandler({
      setErrors,
    });

  const createAssessmentType = useCallback(
    async (
      assessmentType: AssessmentType,
      onSuccess?: (assessmentType: AssessmentType) => void
    ): Promise<{ result: ApiDataResponse<AssessmentType> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = (await new ApiService<AssessmentType>(
          "/assessmentTypes",
          axiosClient
        ).create(assessmentType)) as ApiDataResponse<AssessmentType>;

        if (result.succeeded && result.data) {
          const createdAssessmentType = result.data;
          setAssessmentTypes((prev: AssessmentType[]) =>
            [...prev, createdAssessmentType].sort((a, b) => a.order - b.order)
          );
          handleApiSuccess("Assessment type created successfully");
          onSuccess?.(createdAssessmentType);
          return { result };
        } else {
          handleApiError(result, "Failed to create assessment type");
          return { result: { ...result, data: null as any } };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(
          error,
          "Failed to create assessment type"
        );
        return {
          result: {
            succeeded: false,
            message:
              errorMessage?.message || "Failed to create assessment type",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: null as any,
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess, setAssessmentTypes]
  );

  const updateAssessmentType = useCallback(
    async (
      id: string,
      assessmentType: AssessmentType,
      onSuccess?: (assessmentType: AssessmentType) => void
    ): Promise<{ result: ApiDataResponse<AssessmentType> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = (await new ApiService<AssessmentType>(
          "/assessmentTypes",
          axiosClient
        ).update(id, assessmentType)) as ApiDataResponse<AssessmentType>;

        if (result.succeeded) {
          const updatedAssessmentType = assessmentType;
          setAssessmentTypes((prev: AssessmentType[]) =>
            prev.map((a) => (a.id === id ? updatedAssessmentType : a))
          );
          handleApiSuccess("Assessment type updated successfully");
          onSuccess?.(updatedAssessmentType);
          return { result };
        } else {
          handleApiError(result, "Failed to update assessment type");
          return { result: { ...result, data: null as any } };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(
          error,
          "Failed to update assessment type"
        );
        return {
          result: {
            succeeded: false,
            message:
              errorMessage?.message || "Failed to update assessment type",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: null as any,
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess, setAssessmentTypes]
  );

  const deleteAssessmentType = useCallback(
    async (
      id: string,
      onSuccess?: (id: string) => void
    ): Promise<{ result: ApiResponse }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<AssessmentType>(
          "/assessmentTypes",
          axiosClient
        ).delete(id);
        if (result.succeeded) {
          setAssessmentTypes((prev: AssessmentType[]) =>
            prev.filter((a) => a.id !== id)
          );
          handleApiSuccess("Assessment type deleted successfully");
          onSuccess?.(id);
          return { result };
        } else {
          handleApiError(result, "Failed to delete assessment type");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(
          error,
          "Failed to delete assessment type"
        );
        return {
          result: {
            succeeded: false,
            message:
              errorMessage?.message || "Failed to delete assessment type",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setIsLoading(false);
        setOpenDeleteDialog(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess, setAssessmentTypes]
  );

  return {
    createAssessmentType,
    updateAssessmentType,
    deleteAssessmentType,
    isLoading,
    errors,
    clearErrors,
    openDeleteDialog,
    setOpenDeleteDialog,
  };
};
